/**
 * Consulting Model Configuration
 * COMPLETE extraction from /server/consulting/consulting-api.js
 */

import type { ModelConfig } from '@/types/models';
import { standardResultTabs } from '@/config/resultTabs';

export const consultingConfig: ModelConfig = {
  modelType: 'consulting',
  apiEndpoint: '/api/consulting',
  displayName: 'Consulting',
  resultTabs: standardResultTabs,
  description: 'Professional services model with billable hours, utilization, and project revenue.',
  icon: '💼',
  color: '#14b8a6',

  assumptionGroups: [
    {
      id: 'user-details',
      title: '👤 Your Details',
      fields: [
        { id: 'userEmail', label: 'Your Email', type: 'email', placeholder: 'name@company.com' },
      ],
    },
    {
      id: 'core-consulting',
      title: '💼 Core Consulting Drivers',
      fields: [
        { id: 'initialConsultants', label: 'Starting Consultants', type: 'number', defaultValue: 3 },
        { id: 'avgHourlyRate', label: 'Avg Hourly Rate ($)', type: 'number', defaultValue: 150 },
        { id: 'billableHoursPerMonth', label: 'Billable Hours/Month', type: 'number', defaultValue: 140 },
        { id: 'utilizationRate', label: 'Utilization Rate (%)', type: 'number', defaultValue: 75 },
      ],
    },
    {
      id: 'revenue-mix',
      title: '💰 Revenue Model Mix',
      fields: [
        { id: 'hourlyRevenueShare', label: 'Hourly Revenue (%)', type: 'number', defaultValue: 40, helpText: 'Must sum to 100%' },
        { id: 'projectRevenueShare', label: 'Project Revenue (%)', type: 'number', defaultValue: 35 },
        { id: 'retainerRevenueShare', label: 'Retainer Revenue (%)', type: 'number', defaultValue: 25 },
      ],
    },
    {
      id: 'project-revenue',
      title: '📋 Project-Based Revenue',
      fields: [
        { id: 'avgProjectValue', label: 'Avg Project Value ($)', type: 'number', defaultValue: 25000 },
        { id: 'avgProjectDuration', label: 'Avg Project Duration (mo)', type: 'number', defaultValue: 3 },
        { id: 'projectCompletionRate', label: 'Completion Rate (%)', type: 'number', defaultValue: 90 },
      ],
    },
    {
      id: 'retainer-revenue',
      title: '🔄 Retainer-Based Revenue',
      fields: [
        { id: 'avgRetainerValue', label: 'Avg Retainer Value ($/mo)', type: 'number', defaultValue: 8000 },
        { id: 'initialRetainerClients', label: 'Starting Retainer Clients', type: 'number', defaultValue: 2 },
        { id: 'retainerClientsPerConsultant', label: 'Retainers per Consultant', type: 'number', defaultValue: 2 },
        { id: 'retainerRetentionRate', label: 'Retainer Retention (%)', type: 'number', defaultValue: 85 },
      ],
    },
    {
      id: 'growth',
      title: '📈 Growth & Scaling',
      fields: [
        { id: 'consultantGrowthY2', label: 'Y2 Consultant Growth (%)', type: 'number', defaultValue: 25 },
        { id: 'consultantGrowthY3', label: 'Y3 Consultant Growth (%)', type: 'number', defaultValue: 20 },
        { id: 'consultantGrowthY4', label: 'Y4 Consultant Growth (%)', type: 'number', defaultValue: 15 },
        { id: 'consultantGrowthY5', label: 'Y5 Consultant Growth (%)', type: 'number', defaultValue: 10 },
        { id: 'rateGrowth', label: 'Annual Rate Increase (%)', type: 'number', defaultValue: 5 },
        { id: 'salaryGrowth', label: 'Annual Salary Increase (%)', type: 'number', defaultValue: 3 },
      ],
    },
    {
      id: 'consultant-costs',
      title: '👔 Consultant Compensation',
      fields: [
        { id: 'avgConsultantSalary', label: 'Avg Consultant Salary ($)', type: 'number', defaultValue: 80000 },
        { id: 'benefitsRate', label: 'Benefits Rate (%)', type: 'number', defaultValue: 28 },
      ],
    },
    {
      id: 'overhead',
      title: '🏢 Overhead Costs (Per Consultant)',
      fields: [
        { id: 'officeRentPerConsultant', label: 'Office Rent/Consultant ($/yr)', type: 'number', defaultValue: 2000 },
        { id: 'techCostPerConsultant', label: 'Tech Cost/Consultant ($/yr)', type: 'number', defaultValue: 3000 },
        { id: 'trainingCostPerConsultant', label: 'Training/Consultant ($/yr)', type: 'number', defaultValue: 2500 },
        { id: 'suppliesPerConsultant', label: 'Supplies/Consultant ($/yr)', type: 'number', defaultValue: 500 },
      ],
    },
    {
      id: 'operating-expenses',
      title: '💵 Variable Operating Expenses',
      fields: [
        { id: 'travelExpensePct', label: 'Travel Expenses (%)', type: 'number', defaultValue: 8 },
        { id: 'marketingPct', label: 'Marketing (%)', type: 'number', defaultValue: 10 },
        { id: 'businessDevPct', label: 'Business Dev (%)', type: 'number', defaultValue: 5 },
        { id: 'subcontractorPct', label: 'Subcontractor (%)', type: 'number', defaultValue: 0 },
        { id: 'generalAdminPct', label: 'General Admin (%)', type: 'number', defaultValue: 6 },
        { id: 'initialSetupCosts', label: 'Initial Setup Costs ($)', type: 'number', defaultValue: 25000 },
      ],
    },
    {
      id: 'fixed-assets',
      title: '🏗️ Fixed Assets',
      fields: [
        { id: 'initialEquipmentValue', label: 'Equipment Value ($)', type: 'number', defaultValue: 30000 },
        { id: 'initialFurnitureValue', label: 'Furniture Value ($)', type: 'number', defaultValue: 20000 },
        { id: 'equipmentDepreciationRate', label: 'Equipment Depreciation (%)', type: 'number', defaultValue: 20 },
        { id: 'furnitureDepreciationRate', label: 'Furniture Depreciation (%)', type: 'number', defaultValue: 15 },
        { id: 'monthlyOfficeRent', label: 'Monthly Office Rent ($)', type: 'number', defaultValue: 8000 },
        { id: 'securityDepositMonths', label: 'Security Deposit (mo)', type: 'number', defaultValue: 2 },
      ],
    },
    {
      id: 'debt-interest',
      title: '🏦 Debt & Interest',
      fields: [
        { id: 'initialDebtAmount', label: 'Initial Debt ($)', type: 'number', defaultValue: 100000 },
        { id: 'annualInterestRate', label: 'Interest Rate (%)', type: 'number', defaultValue: 8 },
        { id: 'debtRepaymentYears', label: 'Repayment Years', type: 'number', defaultValue: 5 },
      ],
    },
    {
      id: 'working-capital',
      title: '💰 Working Capital',
      fields: [
        { id: 'daysInAccountsReceivable', label: 'Days in AR', type: 'number', defaultValue: 30 },
        { id: 'daysInAccountsPayable', label: 'Days in AP', type: 'number', defaultValue: 30 },
        { id: 'prepaidExpensesPct', label: 'Prepaid Expenses (%)', type: 'number', defaultValue: 5 },
        { id: 'accruedExpensesPct', label: 'Accrued Expenses (%)', type: 'number', defaultValue: 8 },
        { id: 'minimumCashBalance', label: 'Minimum Cash ($)', type: 'number', defaultValue: 50000 },
      ],
    },
    {
      id: 'capital-raising',
      title: '🚀 Capital Raising',
      fields: [
        { id: 'initialCapitalRaise', label: 'Initial Capital ($)', type: 'number', defaultValue: 300000 },
        { id: 'year2CapitalRaise', label: 'Year 2 Capital ($)', type: 'number', defaultValue: 0 },
        { id: 'year3CapitalRaise', label: 'Year 3 Capital ($)', type: 'number', defaultValue: 150000 },
        { id: 'year4CapitalRaise', label: 'Year 4 Capital ($)', type: 'number', defaultValue: 0 },
        { id: 'year5CapitalRaise', label: 'Year 5 Capital ($)', type: 'number', defaultValue: 0 },
        { id: 'automaticCapitalRaise', label: 'Automatic Capital Raise', type: 'checkbox', defaultValue: true, helpText: 'Automatically raise capital when cash goes negative' },
      ],
    },
    {
      id: 'other-financial',
      title: '📊 Other Financial',
      fields: [
        { id: 'maintenanceCapexPct', label: 'Maintenance CapEx (%)', type: 'number', defaultValue: 2 },
        { id: 'annualInsurance', label: 'Annual Insurance ($)', type: 'number', defaultValue: 15000 },
        { id: 'annualLegalFees', label: 'Annual Legal Fees ($)', type: 'number', defaultValue: 8000 },
      ],
    },
    {
      id: 'financial-tax',
      title: '🏛️ Financial & Tax',
      fields: [
        { id: 'corporateTaxRate', label: 'Corporate Tax Rate (%)', type: 'number', defaultValue: 25 },
      ],
    },
  ],

};

export const consultingDefaultAssumptions = {
  initialConsultants: 3,
  avgHourlyRate: 150,
  billableHoursPerMonth: 140,
  utilizationRate: 75,
  hourlyRevenueShare: 40,
  projectRevenueShare: 35,
  retainerRevenueShare: 25,
  avgProjectValue: 25000,
  avgProjectDuration: 3,
  projectCompletionRate: 90,
  avgRetainerValue: 8000,
  initialRetainerClients: 2,
  retainerClientsPerConsultant: 2,
  retainerRetentionRate: 85,
  consultantGrowthY2: 25,
  consultantGrowthY3: 20,
  consultantGrowthY4: 15,
  consultantGrowthY5: 10,
  rateGrowth: 5,
  salaryGrowth: 3,
  avgConsultantSalary: 80000,
  benefitsRate: 28,
  officeRentPerConsultant: 2000,
  techCostPerConsultant: 3000,
  trainingCostPerConsultant: 2500,
  suppliesPerConsultant: 500,
  travelExpensePct: 8,
  marketingPct: 10,
  businessDevPct: 5,
  subcontractorPct: 0,
  generalAdminPct: 6,
  initialSetupCosts: 25000,
  corporateTaxRate: 25,
  initialEquipmentValue: 30000,
  initialFurnitureValue: 20000,
  equipmentDepreciationRate: 20,
  furnitureDepreciationRate: 15,
  monthlyOfficeRent: 8000,
  securityDepositMonths: 2,
  initialDebtAmount: 100000,
  annualInterestRate: 8,
  debtRepaymentYears: 5,
  daysInAccountsReceivable: 30,
  daysInAccountsPayable: 30,
  prepaidExpensesPct: 5,
  accruedExpensesPct: 8,
  minimumCashBalance: 50000,
  initialCapitalRaise: 300000,
  year2CapitalRaise: 0,
  year3CapitalRaise: 150000,
  year4CapitalRaise: 0,
  year5CapitalRaise: 0,
  automaticCapitalRaise: true,
  maintenanceCapexPct: 2,
  annualInsurance: 15000,
  annualLegalFees: 8000,
  revenueMultiple: 1.2,
  ebitdaMultiple: 6,
  discountRate: 0.12,
  terminalGrowth: 0.02,
};
